import { LitElement, html, css, nothing, unsafeCSS } from 'lit';
import { map } from 'lit/directives/map.js';
import { choose } from 'lit/directives/choose.js';
import { when } from 'lit/directives/when.js';
import { ref, createRef } from 'lit/directives/ref.js';
import { PaperTableStyle } from '../style-modules/paper-table-style.js';
import { FlexStyle } from '../style-modules/flex.js';
import '@polymer/paper-input/paper-input.js';
import '@polymer/paper-toggle-button/paper-toggle-button.js';
import '@polymer/paper-item/paper-item.js';
import '@polymer/iron-collapse/iron-collapse.js';
import '../katapult-elements/katapult-icon.js';
import '../katapult-elements/katapult-button.js';
import '../katapult-drop-down/katapult-drop-down.js';
import '../input-element/input-element.js';
import '../paper-table/paper-table.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';
import { serialize } from '@shoelace-style/shoelace';
import { getColumnOptions, getColumnLabel } from './OverviewColumns.js';

/** @import { Ref } from 'lit/directives/ref.js' */
/** @import SlInput from '@shoelace-style/shoelace/dist/components/input/input.js' */

/**
 * Input for filtering applications in the application overview
 *
 * @fires filters-updated - Fired when the filters are updated
 */
class AppFilterInput extends LitElement {
  static styles = [
    // TODO: Move styles to style modules with Lit CSS tagging
    unsafeCSS(PaperTableStyle),
    unsafeCSS(FlexStyle),
    css`
      #searchFilterContainer {
        width: 400px;
        border-radius: 16px;
        transition: all 0.3s;
        background-color: white;
      }
      #searchFilterContainer[opened] {
        /* From: @apply --shadow-elevation-4dp; */
        box-shadow:
          0 4px 5px 0 rgba(0, 0, 0, 0.14),
          0 1px 10px 0 rgba(0, 0, 0, 0.12),
          0 2px 4px -1px rgba(0, 0, 0, 0.4);
      }
      #searchFilterContainer > iron-collapse {
        border-top: 1px solid var(--paper-grey-200);
      }
      #searchContainer {
        display: flex;
        justify-content: center;
        align-items: center;
        width: inherit;
        padding: 4px 12px;
        box-sizing: border-box;
      }
      #searchContainer > paper-input {
        flex-grow: 1;
      }
      #searchContainer > iron-icon {
        margin: 8px;
      }

      #filterToggle {
        transition: color 0.3s;
      }

      .filter-disclaimer {
        font-size: 10pt;
        padding: 8px 16px;
        text-align: center;
      }

      .filterItem {
        position: relative;
        padding: 12px;
        border-radius: 16px;
        transition:
          background-color 0.2s,
          border 0.2s;
        box-sizing: border-box;
        border: 1px solid rgba(0, 0, 0, 0);
      }
      .filterItem:hover {
        background-color: var(--paper-grey-50);
        border: 1px solid rgba(0, 0, 0, 0.2);
      }
      .filterItem input-element {
        --input-element-paper-checkbox-checkmark-color: white;
      }

      .option paper-cell {
        font-size: 8pt;
      }
      .option paper-cell[active] {
        background-color: var(--secondary-color);
        color: white;
      }
      form {
        display: flex;
        flex-direction: column;
        gap: var(--sl-spacing-small);
      }
    `
  ];

  static properties = {
    showAdvancedFilters: { type: Boolean, reflect: true },
    filteringJobs: { type: Boolean },
    filterAttributes: { type: Array },
    filterAttributeValues: { type: Object },
    searchString: { type: String },
    filteredOverviewAttributes: { type: Array },
    companyAttributes: { type: Object },
    selectedModel: { type: String },
    userGroup: { type: String },
    userId: { type: String },
    showAllJobAttributes: { type: Boolean },
    attachers: { type: Object },
    portalAppStatuses: { type: Array },
    filterOnAdminStrategy: { type: Boolean },
    companies: { type: Object },
    savedFilters: { type: Array },
    selectedSavedFilter: { type: Object },
    workflowVisibility: { type: Array },
    savedFilterToOverwrite: { type: Object }
  };

  constructor() {
    super();
    this.showAdvancedFilters = false;
    this.filteringJobs = false;
    this.filterAttributes = [];
    this.filterAttributeValues = {};
    this.searchString = '';
    this.filteredOverviewAttributes = [];
    this.companyAttributes = {};
    this.selectedModel = '';
    this.userGroup = '';
    this.userId = '';
    this.showAllJobAttributes = false;
    this.attachers = {};
    this.portalAppStatuses = [];
    this.filterOnAdminStrategy = false;
    this.companies = {};
    this.attributeList = [];
    this.savedFilters = [];
    this.selectedSavedFilter = null;
    this.workflowVisibility = [];
    this.savedFilterToOverwrite = null;

    this.showCompletedApps = false;
    this.showDrafts = false;
    this.showArchived = false;

    this._updateSearchStringTimeout = null;
    this._updateFilterAttributeTimeout = null;
  }

  static get specialAttributes() {
    return [{ attribute: 'app_status', label: 'Status' }];
  }

  /** @type {Ref<KatapultDialog>} */
  #createSavedFilterRef = createRef();

  /** @type {Ref<HTMLFormElement>} */
  #createSavedFilterFormRef = createRef();

  render() {
    return html`
      <div id="searchFilterContainer" ?opened="${this.showAdvancedFilters}">
        <div id="searchContainer">
          <div style="width: 35px;">
            ${!this.filteringJobs
              ? html`<katapult-icon icon="search" style="transition: opacity 0.3s ease;"></katapult-icon>`
              : html`<sl-spinner style="font-size: 20px;"></sl-spinner>`}
          </div>
          <paper-input label="Search Applications" no-label-float @value-changed=${this.debouncedSetSearchString}></paper-input>
          <katapult-button
            id="filterToggle"
            icon="filter_list"
            iconOnly
            noBorder
            textColor="${Object.values(this.filterAttributeValues).some((x) => x)
              ? 'var(--secondary-color)'
              : 'var(--primary-text-color-faded)'}"
            title="Advanced Filters"
            @click="${() => (this.showAdvancedFilters = !this.showAdvancedFilters)}"
          ></katapult-button>
        </div>
        <iron-collapse ?opened="${this.showAdvancedFilters}">
          <div style="padding: 12px;">
            ${when(
              this.filterOnAdminStrategy,
              () => html`<div class="filter-disclaimer">(Filtering based on utility administration strategy)</div>`
            )}

            <div style="display: flex; flex-direction: column; gap: var(--sl-spacing-x-small); padding-bottom: var(--sl-spacing-x-small);">
              <div style="font-size: var(--sl-font-size-x-small);">Saved Filters</div>
              <div style="display: flex; align-items: center; gap: var(--sl-spacing-2x-small);">
                <sl-tooltip content="Clear all filters">
                  <katapult-button iconOnly noBorder size="small" icon="clear" @click=${this.clearAllFilters}></katapult-button>
                </sl-tooltip>
                <sl-dropdown style="flex: 1;" ?disabled=${this.savedFilters.length === 0} sync="width">
                  <katapult-button size="small" justify="left" slot="trigger" icon="arrow_drop_down" style="width: 100%;"
                    >${this.selectedSavedFilter != null
                      ? html`<span style="font-weight: bold;">${this.selectedSavedFilter.label}</span>`
                      : html`<span style="color: var(--primary-text-color-faded);"
                          >${this.savedFilters.length === 0 ? 'No saved filters' : 'Select...'}</span
                        >`}</katapult-button
                  >
                  <sl-menu @sl-select="${this.handleSavedFilterSelected}">
                    ${map(this.savedFilters, (filter) => html`<sl-menu-item value=${filter.id}>${filter.label}</sl-menu-item>`)}
                  </sl-menu>
                </sl-dropdown>
                <sl-dropdown>
                  <katapult-button iconOnly noBorder size="small" icon="more_vert" slot="trigger"></katapult-button>
                  <sl-menu @sl-select="${this.handleSavedFilterMenuItemSelected}">
                    <sl-menu-item value="save_as">Save as...</sl-menu-item>
                    ${when(
                      this.selectedSavedFilter != null,
                      () => html`
                        <sl-menu-item value="overwrite">Overwrite...</sl-menu-item>
                        <sl-menu-item value="delete">Delete...</sl-menu-item>
                      `
                    )}
                  </sl-menu>
                </sl-dropdown>
              </div>
            </div>

            <div class="filterItem">
              <paper-toggle-button
                .checked=${this.showCompletedApps}
                @checked-changed="${(e) => this.setAppFilter('completed', e.detail.value)}"
                >Show Completed/Canceled Apps</paper-toggle-button
              >
            </div>
            <div class="filterItem">
              <paper-toggle-button .checked=${this.showDrafts} @checked-changed="${(e) => this.setAppFilter('drafts', e.detail.value)}"
                >Show Drafts</paper-toggle-button
              >
            </div>
            <div class="filterItem">
              <paper-toggle-button .checked=${this.showArchived} @checked-changed="${(e) => this.setAppFilter('archived', e.detail.value)}"
                >Show Archived Apps</paper-toggle-button
              >
            </div>

            <katapult-drop-down
              style="margin: 0px 12px;"
              select-on-focus
              no-label-float
              show-last-results
              label="Add Attribute Filter"
              .items="${this.attributeList}"
              value-path="attribute"
              .labelFunction="${(item) => this.getAttributeLabel(item)}"
              @value-changed="${this.addAttributeFilter}"
            >
              <paper-item @click="${() => (this.showAllJobAttributes = !this.showAllJobAttributes)}">
                ${!this.showAllJobAttributes ? 'Show All Job Attributes' : 'Hide Job Attributes'}
              </paper-item>
            </katapult-drop-down>

            ${map(
              this.filterAttributes,
              (attribute) => html`
                <div class="filterItem">
                  <div flex align-center>
                    <div grow style="font-weight: bold; color: var(--primary-text-color-faded);">${this.getAttributeLabel(attribute)}</div>
                    <div>
                      <paper-table rounded outline overflow-hidden pointer>
                        <paper-row class="option" micro>
                          <paper-cell
                            shrink
                            ?active="${this.filterAttributeValues[attribute.$key] == '$$exists'}"
                            @click="${() => this.setAttributeFilter(attribute.$key, '$$exists')}"
                          >
                            <span>Exists</span>
                          </paper-cell>
                          <paper-cell
                            shrink
                            ?active="${this.filterAttributeValues[attribute.$key] == '$$notexists'}"
                            @click="${() => this.setAttributeFilter(attribute.$key, '$$notexists')}"
                          >
                            <span>Doesn't Exist</span>
                          </paper-cell>
                        </paper-row>
                      </paper-table>
                    </div>
                    <katapult-button
                      class="iconButton"
                      icon="clear"
                      iconOnly
                      noBorder
                      noBackground
                      @click="${() => this.removeAttributeFilter(attribute.$key)}"
                    ></katapult-button>
                  </div>

                  ${!['$$exists', '$$notexists'].includes(this.filterAttributeValues[attribute.$key])
                    ? html`${choose(
                        attribute.$key,
                        [
                          [
                            'app_status',
                            () => html`
                              <katapult-drop-down
                                label="Status"
                                no-label-float
                                show-last-results
                                .items="${this.portalAppStatuses}"
                                label-path="status"
                                value-path="status"
                                secondary-label-path="appType"
                                .sort="${(a, b) => a.order - b.order}"
                                .value="${this.filterAttributeValues.app_status}"
                                @selected-changed="${(e) => this.setAttributeFilter('app_status', e.detail.value)}"
                              ></katapult-drop-down>
                              <div style="font-size: 8pt;">
                                Note: This filter lists statuses from all app types for visibility but does not filter apps by type. Use an
                                "App Type" attribute filter to limit results to an exact app type.
                              </div>
                            `
                          ],
                          [
                            'attachment_owner',
                            () => html`
                              <katapult-drop-down
                                label="${attribute.label}"
                                no-label-float
                                show-last-results
                                .items="${Object.entries(this.attachers)}"
                                label-path="1.name"
                                value-path="0"
                                .value="${this.filterAttributeValues.attachment_owner}"
                                @selected-changed="${(e) => this.setAttributeFilter('attachment_owner', e.detail.value)}"
                              ></katapult-drop-down>
                            `
                          ],
                          [
                            'creator',
                            () => html`
                              <katapult-drop-down
                                label="${attribute.label}"
                                no-label-float
                                show-last-results
                                .items="${this.companies}"
                                label-path="name"
                                value-path="$key"
                                .value="${this.filterAttributeValues.creator}"
                                @selected-changed="${(e) => this.setAttributeFilter('creator', e.detail.value)}"
                              ></katapult-drop-down>
                            `
                          ]
                        ],
                        () =>
                          html`${when(
                            attribute.gui_element == 'date',
                            () => html`
                              <input-element
                                label="Start Date (MM/DD/YYYY)"
                                hide-attribute-name
                                no-label-float
                                .attribute_name="${attribute.$key}"
                                .model="${attribute}"
                                .otherAttributes=${this.companyAttributes}
                                .jobCreator=${this.selectedModel}
                                .userGroup=${this.userGroup}
                                .value="${this.filterAttributeValues[attribute.$key]?._start ?? ''}"
                                @value-changed="${(e) => this.setAttributeFilter(`${attribute.$key}._start`, e.detail.value)}"
                              ></input-element>
                              <input-element
                                label="End Date (MM/DD/YYYY)"
                                hide-attribute-name
                                no-label-float
                                .attribute_name="${attribute.$key}"
                                .model="${attribute}"
                                .otherAttributes=${this.companyAttributes}
                                .jobCreator=${this.selectedModel}
                                .userGroup=${this.userGroup}
                                .value="${this.filterAttributeValues[attribute.$key]?._end ?? ''}"
                                @value-changed="${(e) => this.setAttributeFilter(`${attribute.$key}._end`, e.detail.value)}"
                              ></input-element>
                            `,
                            () => html`
                              <input-element
                                hide-attribute-name
                                no-label-float
                                checkbox-labels
                                ignore-read-only
                                .attribute_name="${attribute.$key}"
                                .model="${attribute}"
                                .otherAttributes=${this.companyAttributes}
                                .jobCreator=${this.selectedModel}
                                .userGroup=${this.userGroup}
                                .value="${this.filterAttributeValues[attribute.$key] ?? ''}"
                                @input="${(e) =>
                                  this.debouncedSetAttributeFilter(attribute.$key, e.target?.shadowRoot.querySelector('#element')?.value)}"
                                @value-changed="${(e) => this.setAttributeFilter(attribute.$key, e.detail.value)}"
                              ></input-element>
                            `
                          )} `
                      )}`
                    : nothing}
                </div>
              `
            )}
          </div>
        </iron-collapse>
      </div>

      ${this.#renderCreateSavedFilterDialog()}
    `;
  }

  #renderCreateSavedFilterDialog() {
    const { savedFilterToOverwrite } = this;
    const overwritingSavedFilter = savedFilterToOverwrite != null;
    const dialogTitle = `${overwritingSavedFilter ? 'Overwrite' : 'Create'} Saved Filter`;
    const confirmButtonLabel = overwritingSavedFilter ? 'Overwrite' : 'Save';
    const shouldIncludeWorkflows = overwritingSavedFilter ? savedFilterToOverwrite.parsedConditions.visibleWorkflowIds?.length > 0 : true;
    return html`
      <katapult-dialog
        ${ref(this.#createSavedFilterRef)}
        title=${dialogTitle}
        @closed=${() => {
          this.savedFilterToOverwrite = null;
          this.#createSavedFilterFormRef.value?.reset();
        }}
      >
        <form ${ref(this.#createSavedFilterFormRef)} @submit=${(e) => e.preventDefault()}>
          ${when(
            overwritingSavedFilter,
            () => html` <div>Are you sure you want to overwrite this saved filter?</div> `,
            () =>
              html`<sl-input
                type="text"
                label="Name"
                name="filter_name"
                placeholder="Name your saved filter"
                required
                minLength="1"
              ></sl-input>`
          )}
          <sl-checkbox name="include_workflows" .checked=${shouldIncludeWorkflows} .defaultChecked=${shouldIncludeWorkflows}
            >Include Currently Selected Workflows</sl-checkbox
          >
        </form>
        <div slot="buttons" style="width: 100%; display: flex; justify-content: space-between;">
          <katapult-button outlined @click=${() => this.#createSavedFilterRef.value?.close()}>Cancel</katapult-button>
          <katapult-button color="var(--secondary-color)" .callback=${this.saveInputsAsFilter.bind(this)}
            >${confirmButtonLabel}</katapult-button
          >
        </div>
      </katapult-dialog>
    `;
  }

  willUpdate(changedProperties) {
    // Update the attribute list
    if (changedProperties.has('showAllJobAttributes') || changedProperties.has('filteredOverviewAttributes')) {
      // Set the attribute list
      this.updateAttributeList();
    }

    // Update the saved filters listener
    if (changedProperties.has('userGroup') || changedProperties.has('userId')) {
      this.updateSavedFiltersListener();
    }
  }

  updateAttributeList() {
    const attributeList = [...(this.filteredOverviewAttributes ?? [])];
    if (this.showAllJobAttributes) {
      // For each option, add it to the list if it doesn't already exist
      const columnOptions = getColumnOptions(this.companyAttributes);
      columnOptions.forEach((option) => {
        const optionAlreadyExists = attributeList.some((x) => x.attribute == option.value);
        if (optionAlreadyExists) return;
        attributeList.push({ attribute: option.value });
      });
    }

    // If the attribute list is missing any special attributes, add them
    for (const specialAttribute of AppFilterInput.specialAttributes) {
      if (!attributeList.some((x) => x.attribute == specialAttribute.attribute)) attributeList.push(specialAttribute);
    }

    this.attributeList = attributeList;
  }

  dispatchFiltersUpdatedEvent() {
    const event = new CustomEvent('filters-updated', {
      detail: {
        value: {
          searchString: this.searchString,
          showCompletedApps: this.showCompletedApps,
          showDrafts: this.showDrafts,
          showArchived: this.showArchived,
          filterAttributeValues: this.filterAttributeValues
        }
      }
    });
    this.dispatchEvent(event);
  }

  debouncedSetSearchString(e) {
    if (this._updateSearchStringTimeout != null) clearTimeout(this._updateSearchStringTimeout);
    this._updateSearchStringTimeout = setTimeout(() => {
      this.searchString = e.detail.value;
      this.dispatchFiltersUpdatedEvent();
    }, 200);
  }

  setAppFilter(filterType, value) {
    switch (filterType) {
      case 'completed':
        this.showCompletedApps = value;
        break;
      case 'drafts':
        this.showDrafts = value;
        break;
      case 'archived':
        this.showArchived = value;
        break;
    }
    this.dispatchFiltersUpdatedEvent();
  }

  addAttributeFilter(e) {
    const attribute = e.detail.value;
    const attributeAlreadyInFilterArray = this.filterAttributes.some((x) => x.$key == attribute);
    // If the attribute identifier is defined and the filter attribute is not already being used
    if (attribute && !attributeAlreadyInFilterArray) {
      const newFilterAttribute = this.buildFilterAttribute(attribute);
      this.filterAttributes = [...this.filterAttributes, newFilterAttribute];
    }

    // Clear the dropdown selection
    e.target.value = '';
  }

  buildFilterAttribute(attribute) {
    const label = getColumnLabel(attribute, {
      overviewAttributes: this.filteredOverviewAttributes,
      attributeModels: this.companyAttributes
    });
    const filterAttribute = { label };
    const attributeModel = this.companyAttributes[attribute];
    if (attributeModel) {
      Object.assign(filterAttribute, attributeModel);
    } else {
      // Fake an attribute model for column options which don't refer to an attribute.
      Object.assign(filterAttribute, { gui_element: 'textbox' });
    }
    filterAttribute.$key = attribute;
    return filterAttribute;
  }

  removeAttributeFilter(attribute) {
    // Remove the attribute from the filter attributes list
    this.filterAttributes = this.filterAttributes.filter((x) => x.$key != attribute);
    // Remove the attribute from the filter attribute values
    this.setAttributeFilter(attribute, null);
  }

  setAttributeFilter(attributePath, value) {
    const [attribute, subProperty] = attributePath.split('.');
    // Set the filter attribute value
    const currentValue = this.filterAttributeValues[attribute];
    // Re-selecting the same "existence" value should clear the filter
    if (currentValue == value && ['$$exists', '$$notexists'].includes(value)) value = null;
    // If there is a sub property, apply the value to it
    if (subProperty) value = { ...currentValue, [subProperty]: value };

    this.filterAttributeValues = { ...this.filterAttributeValues, [attribute]: value };
    this.dispatchFiltersUpdatedEvent();
  }

  debouncedSetAttributeFilter(attribute, value) {
    if (this._updateFilterAttributeTimeout != null) clearTimeout(this._updateFilterAttributeTimeout);
    this._updateFilterAttributeTimeout = setTimeout(() => {
      this.setAttributeFilter(attribute, value);
    }, 200);
  }

  getAttributeLabel(attribute) {
    const attributeName = attribute?.attribute ?? attribute?.$key;
    return getColumnLabel(attributeName, { overviewAttributes: this.filteredOverviewAttributes, attributeModels: this.companyAttributes });
  }

  async handleSavedFilterMenuItemSelected(event) {
    const selectedMenuItem = event.detail.item.value;

    switch (selectedMenuItem) {
      case 'save_as': {
        // Open the create saved filter dialog
        this.#createSavedFilterRef.value?.open();
        return;
      }
      case 'overwrite': {
        this.savedFilterToOverwrite = this.selectedSavedFilter;
        // Open the create saved filter dialog
        this.#createSavedFilterRef.value?.open();
        return;
      }
      case 'delete': {
        const didConfirm = await KatapultDialog.confirm('Are you sure you want to delete this saved filter?').confirmed;
        if (!didConfirm) return;

        // Make sure we have the user group and user id
        if (this.userGroup == null || this.userId == null) return;

        // Delete the saved filter
        await this.savedFiltersRef.child(this.selectedSavedFilter.id).remove();
        this.selectedSavedFilter = null;
        return;
      }
    }
  }

  handleSavedFilterSelected(event) {
    const selectedFilterId = event.detail.item.value;
    this.loadSavedFilterById(selectedFilterId);
  }

  loadSavedFilterById(id) {
    const filterData = this.savedFilters.find((filter) => filter.id == id);
    this.loadSavedFilterData(filterData);
  }

  loadSavedFilterData(filterData) {
    // Parse the conditions and assign them to the filter attributes
    const conditions = (filterData.parsedConditions = JSON.parse(filterData.conditions));
    const { showCompletedApps, showDrafts, showArchived, filterAttributeValues, showAllJobAttributes, visibleWorkflowIds } = conditions;

    Object.assign(this, {
      selectedSavedFilter: filterData,
      showCompletedApps,
      showDrafts,
      showArchived,
      filterAttributeValues,
      showAllJobAttributes
    });

    this.dispatchEvent(new CustomEvent('saved-filter-loaded', { detail: { savedFilter: filterData } }));

    // Re-generate the filter attributes inputs from the values object
    this.updateAttributeList();
    this.filterAttributes = Object.keys(this.filterAttributeValues ?? {}).map((attribute) => this.buildFilterAttribute(attribute));

    // Dispatch the filters updated event
    this.dispatchFiltersUpdatedEvent();
  }

  async saveInputsAsFilter() {
    // Make sure we have a form reference
    if (this.#createSavedFilterFormRef.value == null) return;
    // Make sure we have the user group and user id
    if (this.userGroup == null || this.userId == null) return;

    const isValid = this.#createSavedFilterFormRef.value.reportValidity();
    if (!isValid) return;

    const { savedFilterToOverwrite } = this;

    const formData = serialize(this.#createSavedFilterFormRef.value);
    const filterName = savedFilterToOverwrite?.label ?? formData.filter_name;
    const includeWorkflows = formData.include_workflows === 'on';
    const filterData = this.getSavedFilterData(filterName, includeWorkflows);

    if (savedFilterToOverwrite) {
      // Overwrite the saved filter
      await this.savedFiltersRef.child(savedFilterToOverwrite.id).set(filterData);
      this.loadSavedFilterData({ ...filterData, id: savedFilterToOverwrite.id });
    } else {
      const { key } = await this.savedFiltersRef.push(filterData);
      this.loadSavedFilterData({ ...filterData, id: key });
    }

    this.#createSavedFilterRef.value?.close();
  }

  getSavedFilterData(filterName, includeWorkflows = false) {
    // Remove null values from the filter attribute values
    const nonNullFilterAttributeValues = Object.fromEntries(Object.entries(this.filterAttributeValues).filter((x) => x[1] != null));

    const conditions = {};

    // These properties match their Lit properties names exactly for later re-assignment
    conditions.showCompletedApps = this.showCompletedApps;
    conditions.showDrafts = this.showDrafts;
    conditions.showArchived = this.showArchived;
    conditions.filterAttributeValues = nonNullFilterAttributeValues;
    conditions.showAllJobAttributes = this.showAllJobAttributes;

    if (includeWorkflows) {
      const visibleWorkflowIds = this.workflowVisibility
        ?.filter((workflow) => workflow.visible)
        .map((workflow) => workflow.workflowId)
        .filter((x) => !!x);
      conditions.visibleWorkflowIds = visibleWorkflowIds;
    }

    return {
      label: filterName,
      model: this.selectedModel,
      conditions: JSON.stringify(conditions)
    };
  }

  updateSavedFiltersListener() {
    // Make sure we have the user group and user id
    if (this.userGroup == null || this.userId == null) {
      if (typeof this.unlistenSavedFilters === 'function') this.unlistenSavedFilters();
      this.savedFilters = [];
      this.savedFiltersRef = null;
      this.unlistenSavedFilters = null;
      return;
    }

    // Listen for saved filters
    const realtimeDb = globalThis.FirebaseWorker.database();
    const pathToSavedFilters = `photoheight/company_space/${this.userGroup}/user_data/${this.userId}/overview_saved_filters`;
    const savedFiltersRef = realtimeDb.ref(pathToSavedFilters);

    const callbackForUnlisten = savedFiltersRef.on('value', (snapshot) => {
      const savedFilters = snapshot.val();
      this.savedFilters = Object.entries(savedFilters ?? {}).map(([id, value]) => ({ id, ...value }));
    });
    this.unlistenSavedFilters = () => savedFiltersRef.off('value', callbackForUnlisten);

    // Make the reference available to other methods
    this.savedFiltersRef = savedFiltersRef;
  }

  clearAllFilters() {
    this.showCompletedApps = false;
    this.showDrafts = false;
    this.showArchived = false;
    this.filterAttributes = [];
    this.filterAttributeValues = {};
    this.selectedSavedFilter = null;
    this.dispatchFiltersUpdatedEvent();
  }
}

window.customElements.define('app-filter-input', AppFilterInput);
